/**
 * Financeiro do grupo — aba "Financeiro" dentro da pelada.
 *
 * Mora no grupo, e não numa tela global com seletor: mensalidade, cobrança
 * avulsa e caixa são sempre de uma pelada específica, e é ali que o admin já
 * está quando precisa lançar algo.
 *
 * A tela grande é onde o financeiro faz mais sentido: o admin consegue ver
 * saldo, série mensal, mensalidades e despesas ao mesmo tempo — no celular
 * isso vira rolagem. Quando o grupo restringe o caixa aos administradores, o
 * participante ainda vê as próprias mensalidades, que são informação dele.
 */

import { feeTitle, formatCurrency, toCents } from '@nossorachao/utils';
import {
  ChevronLeft,
  ChevronRight,
  ArrowDownCircle,
  ArrowUpCircle,
  FileText,
  Lock,
  Upload,
  Plus,
  QrCode,
  Receipt,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useRef, useState } from 'react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  SectionTitle,
  Skeleton,
  StatTile,
  Tabs,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  useCancelFee,
  useCreateCharge,
  useCreateExpense,
  useCreateIncome,
  useCreatePixCharge,
  useDeleteTransaction,
  useFees,
  useFinancial,
  useGroup,
  openReceipt,
  useMembers,
  useMyFees,
  useRegisterManualPayment,
  useRevertPayment,
  useFinancialReport,
  useGenerateFees,
  useSubmitReceipt,
} from '@/lib/queries';
import { Modal } from './groups';

const FEE_STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }> = {
  PAID: { label: 'Pago', tone: 'ok' },
  PENDING: { label: 'Pendente', tone: 'warn' },
  OVERDUE: { label: 'Atrasado', tone: 'bad' },
  WAIVED: { label: 'Isento', tone: 'neutral' },
  CANCELED: { label: 'Cancelado', tone: 'neutral' },
};

export function GroupFinancial({ groupId, isAdmin }: { groupId: string; isAdmin: boolean }) {
  const group = useGroup(groupId);
  const overview = useFinancial(groupId);
  const myFees = useMyFees(groupId);
  const allFees = useFees(groupId);
  const [entryModal, setEntryModal] = useState<EntryKind | null>(null);
  const [chargeModal, setChargeModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState<{
    paymentId: string;
    memberName: string;
    referenceMonth: string;
    amountCents: number;
  } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{
    feeId: string;
    memberName: string;
    title: string;
    amountCents: number;
  } | null>(null);
  const [pixCharge, setPixCharge] = useState<{
    feeId: string;
    brCode: string;
    qrCodeImage: string;
    autoConfirm: boolean;
  } | null>(null);

  // 403 aqui não é falha: é o grupo dizendo que o caixa é restrito.
  const hidden = overview.error instanceof ApiError && overview.error.code === 'FINANCIAL_HIDDEN';

  return (
    <>
      {isAdmin ? (
        <div className="mb-6 flex flex-wrap gap-2">
          <Button size="sm" icon={Receipt} onClick={() => setChargeModal(true)}>
            Nova cobrança
          </Button>
          <Button
            size="sm"
            variant="secondary"
            icon={Plus}
            onClick={() => setEntryModal('INCOME')}
          >
            Lançar entrada
          </Button>
          <Button
            size="sm"
            variant="secondary"
            icon={Plus}
            onClick={() => setEntryModal('EXPENSE')}
          >
            Lançar despesa
          </Button>
        </div>
      ) : null}

      {/* Mensalidades do grupo — só admin, e primeiro: é o que o admin gerencia. */}
      {isAdmin ? (
        <section className="mb-8 space-y-3">
          <SectionTitle action={<GenerateFeesButton groupId={groupId} />}>
            Mensalidades e cobranças
          </SectionTitle>
          {allFees.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : allFees.error ? (
            <Card>
              <p className="text-sm text-bad">
                Não foi possível carregar as mensalidades
                {allFees.error instanceof ApiError ? `: ${allFees.error.message}` : '.'}
              </p>
            </Card>
          ) : !allFees.data?.length ? (
            <Card>
              <p className="text-sm text-ink-2">
                Nenhuma mensalidade lançada. Elas são geradas automaticamente no dia 1 de cada
                mês — use “Gerar mensalidades” para lançar o mês atual agora.
              </p>
            </Card>
          ) : (
            <Card className="overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-[13px] text-ink-3">
                  <tr>
                    <th className="p-3 font-semibold">Participante</th>
                    <th className="p-3 font-semibold">Cobrança</th>
                    <th className="p-3 font-semibold">Valor</th>
                    <th className="p-3 font-semibold">Situação</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {allFees.data.slice(0, 30).map((fee) => {
                    const status = FEE_STATUS[fee.status] ?? FEE_STATUS.PENDING!;
                    return (
                      <tr key={fee.id}>
                        <td className="truncate p-3">{fee.member.name}</td>
                        <td className="p-3 text-ink-2">{feeTitle(fee)}</td>
                        <td className="tabular p-3 font-semibold">
                          {formatCurrency(fee.amountCents)}
                        </td>
                        <td className="p-3">
                          <Badge tone={status.tone}>{status.label}</Badge>
                        </td>
                        <td className="space-x-2 whitespace-nowrap p-3 text-right">
                          {fee.payment?.hasReceipt && fee.payment.status === 'CONFIRMED' ? (
                            <ViewReceiptButton
                              groupId={groupId}
                              paymentId={fee.payment.id}
                              inline
                            />
                          ) : null}
                          {fee.status === 'PAID' && fee.payment?.revertible ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                setRevertTarget({
                                  paymentId: fee.payment!.id,
                                  memberName: fee.member.name,
                                  referenceMonth: fee.referenceMonth,
                                  amountCents: fee.amountCents,
                                })
                              }
                            >
                              Marcar como não pago
                            </Button>
                          ) : null}
                          {fee.status === 'PENDING' || fee.status === 'OVERDUE' ? (
                            <ConfirmPaymentButton
                              groupId={groupId}
                              feeId={fee.id}
                              amountCents={fee.amountCents}
                              pixPending={
                                fee.payment?.method === 'PIX' &&
                                fee.payment.status === 'PENDING'
                              }
                            />
                          ) : null}
                          {fee.status === 'PENDING' || fee.status === 'OVERDUE' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setCancelTarget({
                                  feeId: fee.id,
                                  memberName: fee.member.name,
                                  title: feeTitle(fee),
                                  amountCents: fee.amountCents,
                                })
                              }
                            >
                              Cancelar
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </section>
      ) : null}

      {/* Para o participante, as próprias mensalidades vêm primeiro. */}
      <section className="mb-8 space-y-3">
        <SectionTitle>Minhas mensalidades</SectionTitle>

        {myFees.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : myFees.error ? (
          <Card>
            <p className="text-sm text-bad">
              Não foi possível carregar suas mensalidades
              {myFees.error instanceof ApiError ? `: ${myFees.error.message}` : '.'}
            </p>
          </Card>
        ) : !myFees.data?.length ? (
          <Card>
            <p className="text-sm text-ink-2">Nenhuma mensalidade lançada neste grupo.</p>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {myFees.data.slice(0, 6).map((fee) => {
              const status = FEE_STATUS[fee.status] ?? FEE_STATUS.PENDING!;
              const payable = fee.status === 'PENDING' || fee.status === 'OVERDUE';

              return (
                <Card key={fee.id}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{feeTitle(fee)}</p>
                      <p className="text-[13px] text-ink-3">
                        Vence em{' '}
                        {new Intl.DateTimeFormat('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                        }).format(new Date(fee.dueDate))}
                      </p>
                    </div>
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </div>

                  <p className="tabular mt-3 text-xl font-extrabold">
                    {formatCurrency(fee.amountCents)}
                  </p>

                  {payable ? (
                    <>
                      <PixButton groupId={groupId} feeId={fee.id} onCharge={setPixCharge} />
                      <ReceiptButton groupId={groupId} feeId={fee.id} />
                    </>
                  ) : null}

                  {fee.status === 'PAID' && fee.payment?.hasReceipt ? (
                    <ViewReceiptButton groupId={groupId} paymentId={fee.payment.id} />
                  ) : null}
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {hidden ? (
        <Card>
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 shrink-0 text-ink-3" aria-hidden />
            <p className="text-sm text-ink-2">
              O caixa deste grupo está visível apenas para os administradores.
            </p>
          </div>
        </Card>
      ) : overview.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : overview.error ? (
        <Card>
          <p className="text-sm text-bad">
            Não foi possível carregar o caixa
            {overview.error instanceof ApiError ? `: ${overview.error.message}` : '.'}
          </p>
        </Card>
      ) : overview.data ? (
        <div className="space-y-8">
          <section className="space-y-3">
            <SectionTitle>Caixa do grupo</SectionTitle>

            <div className="grid gap-4 sm:grid-cols-3">
              <StatTile
                label="Saldo atual"
                value={formatCurrency(overview.data.balanceCents)}
                tone={overview.data.balanceCents >= 0 ? 'ok' : 'bad'}
                icon={Wallet}
              />
              <StatTile
                label="Receitas do mês"
                value={formatCurrency(overview.data.monthIncomeCents)}
                tone="ok"
                icon={ArrowUpCircle}
              />
              <StatTile
                label="Despesas do mês"
                value={formatCurrency(overview.data.monthExpenseCents)}
                tone="bad"
                icon={ArrowDownCircle}
              />
            </div>

            {/* Só para admin: o jogador que já pagou lia "em aberto" como dívida dele. */}
            {isAdmin &&
            (overview.data.pendingFeesCount > 0 || overview.data.overdueFeesCount > 0) ? (
              <Card accent="rgb(220 104 3)">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">Mensalidades em aberto</p>
                    <p className="text-[13px] text-ink-2">
                      {overview.data.pendingFeesCount} pendente(s)
                      {overview.data.overdueFeesCount > 0
                        ? ` · ${overview.data.overdueFeesCount} atrasada(s)`
                        : ''}
                    </p>
                  </div>
                  <p className="tabular text-lg font-extrabold text-warn">
                    {formatCurrency(
                      overview.data.pendingFeesCents + overview.data.overdueFeesCents,
                    )}
                  </p>
                </div>
              </Card>
            ) : null}
          </section>

          {/* Série mensal — barras em CSS puro, sem biblioteca de gráfico. */}
          {overview.data.series.length > 0 ? (
            <section className="space-y-3">
              <SectionTitle>Receitas × Despesas</SectionTitle>
              <Card>
                <div className="space-y-4">
                  {overview.data.series.map((month) => {
                    const peak = Math.max(
                      ...overview.data!.series.flatMap((entry) => [
                        entry.incomeCents,
                        entry.expenseCents,
                      ]),
                      1,
                    );

                    return (
                      <div key={month.month} className="space-y-1.5">
                        <div className="flex items-center justify-between text-[13px]">
                          <span className="text-ink-2">{formatReference(month.month)}</span>
                          <span
                            className={`tabular font-semibold ${
                              month.balanceCents >= 0 ? 'text-ok' : 'text-bad'
                            }`}
                          >
                            {formatCurrency(month.balanceCents)}
                          </span>
                        </div>

                        <div className="space-y-1">
                          <Bar value={month.incomeCents} peak={peak} className="bg-ok" />
                          <Bar value={month.expenseCents} peak={peak} className="bg-bad" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex gap-4 border-t border-line pt-3 text-[13px] text-ink-3">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-ok" /> Receitas
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-bad" /> Despesas
                  </span>
                </div>
              </Card>
            </section>
          ) : null}

          <MonthlyReport groupId={groupId} isAdmin={isAdmin} />
        </div>
      ) : null}

      {chargeModal && group.data ? (
        <ChargeModal
          groupId={groupId}
          defaultAmountCents={group.data.monthlyFeeCents}
          dueDay={group.data.dueDayOfMonth}
          onClose={() => setChargeModal(false)}
        />
      ) : null}

      {entryModal ? (
        <EntryModal
          groupId={groupId}
          initialKind={entryModal}
          onClose={() => setEntryModal(null)}
        />
      ) : null}

      {cancelTarget ? (
        <CancelFeeModal
          groupId={groupId}
          target={cancelTarget}
          onClose={() => setCancelTarget(null)}
        />
      ) : null}

      {revertTarget ? (
        <RevertModal
          groupId={groupId}
          target={revertTarget}
          onClose={() => setRevertTarget(null)}
        />
      ) : null}

      {pixCharge ? (
        <Modal title="Pagar com Pix" onClose={() => setPixCharge(null)}>
          <div className="space-y-4 text-center">
            <img
              src={pixCharge.qrCodeImage}
              alt="QR Code do Pix"
              className="mx-auto h-56 w-56 rounded-lg bg-white p-2"
            />
            <Field label="Pix Copia e Cola">
              <Input readOnly value={pixCharge.brCode} className="font-mono text-[11px]" />
            </Field>
            <Button
              full
              onClick={() => {
                void navigator.clipboard.writeText(pixCharge.brCode);
              }}
            >
              Copiar código
            </Button>
            <p className="text-[13px] text-ink-3">
              {pixCharge.autoConfirm
                ? 'A confirmação é automática — o provedor avisa o servidor assim que o pagamento cai.'
                : 'O valor cai direto na conta do grupo. Depois de pagar, envie o comprovante para a mensalidade ficar paga.'}
            </p>
            {pixCharge.autoConfirm ? null : (
              <ReceiptButton
                groupId={groupId}
                feeId={pixCharge.feeId}
                onDone={() => setPixCharge(null)}
              />
            )}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function Bar({ value, peak, className }: { value: number; peak: number; className: string }) {
  return (
    <div className="h-2 overflow-hidden rounded-pill bg-sunken">
      <div
        className={`h-full ${className}`}
        style={{ width: `${Math.min(100, (value / peak) * 100)}%` }}
      />
    </div>
  );
}

function PixButton({
  groupId,
  feeId,
  onCharge,
}: {
  groupId: string;
  feeId: string;
  onCharge: (charge: {
    feeId: string;
    brCode: string;
    qrCodeImage: string;
    autoConfirm: boolean;
  }) => void;
}) {
  const create = useCreatePixCharge(groupId);

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        icon={QrCode}
        full
        className="mt-3"
        loading={create.isPending}
        onClick={async () => {
          const charge = await create.mutateAsync(feeId);
          onCharge({
            feeId,
            brCode: charge.brCode,
            qrCodeImage: charge.qrCodeImage,
            autoConfirm: charge.autoConfirm,
          });
        }}
      >
        Pagar com Pix
      </Button>

      {create.error ? (
        <p className="mt-1.5 text-[12px] text-bad">
          {create.error instanceof ApiError ? create.error.message : 'Erro ao gerar cobrança.'}
        </p>
      ) : null}
    </>
  );
}

/**
 * Baixa feita pelo admin depois de conferir o extrato.
 *
 * Se o participante gerou o QR, a baixa fecha aquela cobrança Pix; senão vira
 * transferência. A API recusa Pix aqui quando há PSP confirmando sozinho.
 */
function ConfirmPaymentButton({
  groupId,
  feeId,
  amountCents,
  pixPending,
}: {
  groupId: string;
  feeId: string;
  amountCents: number;
  pixPending: boolean;
}) {
  const register = useRegisterManualPayment(groupId);

  return (
    <div className="inline-flex flex-col items-end">
      <Button
        size="sm"
        variant="secondary"
        loading={register.isPending}
        onClick={() =>
          register.mutate({
            feeId,
            method: pixPending ? 'PIX' : 'TRANSFER',
            amountCents,
            paidAt: todayISO(),
          })
        }
      >
        Confirmar pagamento
      </Button>

      {register.error ? (
        <p className="mt-1 text-[12px] text-bad">
          {register.error instanceof ApiError ? register.error.message : 'Erro ao dar baixa.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Envio do comprovante pelo próprio participante.
 *
 * A mensalidade fica paga no envio. Se o Pix não aparecer na conta do grupo,
 * o administrador marca como não pago.
 */
function ReceiptButton({
  groupId,
  feeId,
  onDone,
}: {
  groupId: string;
  feeId: string;
  onDone?: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const submit = useSubmitReceipt(groupId);

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          submit.mutate({ feeId, file }, { onSuccess: () => onDone?.() });
        }}
      />
      <Button
        size="sm"
        variant="ghost"
        icon={Upload}
        full
        className="mt-2"
        loading={submit.isPending}
        onClick={() => input.current?.click()}
      >
        Já paguei — enviar comprovante
      </Button>

      {submit.error ? (
        <p className="mt-1.5 text-[12px] text-bad">
          {submit.error instanceof ApiError ? submit.error.message : 'Erro ao enviar comprovante.'}
        </p>
      ) : null}
    </>
  );
}

function ViewReceiptButton({
  groupId,
  paymentId,
  inline = false,
}: {
  groupId: string;
  paymentId: string;
  inline?: boolean;
}) {
  const [loading, setLoading] = useState(false);

  return (
    <Button
      size="sm"
      variant="ghost"
      icon={FileText}
      full={!inline}
      className={inline ? undefined : 'mt-3'}
      loading={loading}
      onClick={async () => {
        setLoading(true);
        try {
          await openReceipt(groupId, paymentId);
        } finally {
          setLoading(false);
        }
      }}
    >
      Ver comprovante
    </Button>
  );
}

/**
 * Lança as mensalidades do mês corrente para todos os participantes ativos.
 * O job do dia 1 faz o mesmo; o botão cobre o grupo criado no meio do mês e
 * quem entrou depois da geração. A API ignora quem já tem mensalidade no mês.
 */
function GenerateFeesButton({ groupId }: { groupId: string }) {
  const generate = useGenerateFees(groupId);
  const month = todayISO().slice(0, 7);

  return (
    <div className="flex flex-col items-end">
      <Button
        size="sm"
        variant="secondary"
        icon={Plus}
        loading={generate.isPending}
        onClick={() => generate.mutate(month)}
      >
        Gerar mensalidades de {formatReference(month)}
      </Button>
      {generate.data ? (
        <p className="mt-1 text-[12px] text-ok">
          {generate.data.created > 0
            ? `${generate.data.created} lançada(s) · ${formatCurrency(generate.data.totalCents)}`
            : 'Todos já tinham mensalidade neste mês.'}
        </p>
      ) : null}
      {generate.error ? (
        <p className="mt-1 text-[12px] text-bad">
          {generate.error instanceof ApiError ? generate.error.message : 'Erro ao gerar.'}
        </p>
      ) : null}
    </div>
  );
}

/** Admin conferiu a conta e o Pix não entrou: reabre a mensalidade e estorna o caixa. */
function RevertModal({
  groupId,
  target,
  onClose,
}: {
  groupId: string;
  target: { paymentId: string; memberName: string; referenceMonth: string; amountCents: number };
  onClose: () => void;
}) {
  const revert = useRevertPayment(groupId);
  const [reason, setReason] = useState('');

  return (
    <Modal title="Marcar como não pago" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-ink-2">
          A mensalidade de <strong>{target.memberName}</strong> (
          {formatReference(target.referenceMonth)}, {formatCurrency(target.amountCents)}) volta a
          ficar em aberto, o valor sai do caixa e o participante é avisado.
        </p>
        <Field label="Motivo (opcional)">
          <Input
            value={reason}
            maxLength={160}
            placeholder="Ex.: Pix não apareceu no extrato"
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        <Button
          full
          variant="danger"
          loading={revert.isPending}
          onClick={() =>
            revert.mutate(
              { paymentId: target.paymentId, reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          Marcar como não pago
        </Button>
        {revert.error ? (
          <p className="text-[12px] text-bad">
            {revert.error instanceof ApiError ? revert.error.message : 'Erro ao desfazer.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  FIELD_RENTAL: 'Aluguel do campo',
  WATER: 'Água',
  BIBS: 'Coletes',
  BALL: 'Bola',
  REFEREE: 'Arbitragem',
  BARBECUE: 'Churrasco',
  EQUIPMENT: 'Equipamento',
  OTHER: 'Outros',
};

function shiftMonth(month: string, delta: number): string {
  const [year, value] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 2026, (value ?? 1) - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Fechamento do mês — o que o participante vê para saber para onde foi o
 * dinheiro. Só totais: quem pagou ou deixou de pagar é assunto do admin, na
 * tabela de mensalidades.
 */
function MonthlyReport({ groupId, isAdmin }: { groupId: string; isAdmin: boolean }) {
  const current = todayISO().slice(0, 7);
  const [month, setMonth] = useState(current);
  const report = useFinancialReport(groupId, month);
  const data = report.data;
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    description: string;
    amountCents: number;
    outflow: boolean;
  } | null>(null);

  const categoryPeak = Math.max(
    ...(data?.expensesByCategory.map((row) => row.totalCents) ?? []),
    1,
  );

  return (
    <section className="space-y-3">
      <SectionTitle
        action={
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              icon={ChevronLeft}
              aria-label="Mês anterior"
              onClick={() => setMonth(shiftMonth(month, -1))}
            />
            <span className="min-w-[128px] text-center text-[13px] font-semibold">
              {formatReference(month)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              icon={ChevronRight}
              aria-label="Próximo mês"
              disabled={month >= current}
              onClick={() => setMonth(shiftMonth(month, 1))}
            />
          </div>
        }
      >
        Relatório do mês
      </SectionTitle>

      {report.isLoading || !data ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Saldo no início" value={formatCurrency(data.openingBalanceCents)} />
            <StatTile
              label="Receitas"
              value={formatCurrency(data.incomeCents)}
              tone="ok"
              icon={ArrowUpCircle}
              hint={`${formatCurrency(data.feeIncomeCents)} de mensalidades e rachas`}
            />
            <StatTile
              label="Despesas"
              value={formatCurrency(data.expenseCents)}
              tone="bad"
              icon={ArrowDownCircle}
            />
            <StatTile
              label="Saldo no fim"
              value={formatCurrency(data.closingBalanceCents)}
              tone={data.closingBalanceCents >= 0 ? 'ok' : 'bad'}
              icon={Wallet}
              hint={`${data.resultCents >= 0 ? '+' : '−'} ${formatCurrency(Math.abs(data.resultCents))} no mês`}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <h3 className="mb-3 font-bold">Mensalidades</h3>
              {data.fees.paidCount + data.fees.openCount === 0 ? (
                <p className="text-sm text-ink-3">Nenhuma mensalidade lançada neste mês.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-ink-2">
                      {data.fees.paidCount} de {data.fees.paidCount + data.fees.openCount} pagas
                    </span>
                    <span className="tabular font-semibold">
                      {formatCurrency(data.fees.paidCents)} de{' '}
                      {formatCurrency(data.fees.expectedCents)}
                    </span>
                  </div>
                  <Bar
                    value={data.fees.paidCents}
                    peak={Math.max(data.fees.expectedCents, 1)}
                    className="bg-ok"
                  />
                  {isAdmin && data.fees.openCount > 0 ? (
                    <p className="text-[13px] text-warn">
                      {data.fees.openCount} em aberto · {formatCurrency(data.fees.openCents)}
                    </p>
                  ) : null}
                </div>
              )}
            </Card>

            <Card>
              <h3 className="mb-3 font-bold">Despesas por categoria</h3>
              {!data.expensesByCategory.length ? (
                <p className="text-sm text-ink-3">Nenhuma despesa neste mês.</p>
              ) : (
                <div className="space-y-3">
                  {data.expensesByCategory.map((row) => (
                    <div key={row.category} className="space-y-1">
                      <div className="flex justify-between text-[13px]">
                        <span className="text-ink-2">
                          {CATEGORY_LABELS[row.category] ?? row.category}
                        </span>
                        <span className="tabular font-semibold">
                          {formatCurrency(row.totalCents)}
                        </span>
                      </div>
                      <Bar value={row.totalCents} peak={categoryPeak} className="bg-bad" />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <Card className="p-0">
            <h3 className="border-b border-line p-4 font-bold">Lançamentos do mês</h3>
            {!data.transactions.length ? (
              <p className="p-4 text-sm text-ink-3">Nenhum lançamento neste mês.</p>
            ) : (
              <ul className="divide-y divide-line">
                {data.transactions.map((entry) => {
                  const outflow = entry.type === 'EXPENSE';
                  return (
                    <li key={entry.id} className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <p
                          className={`truncate font-semibold ${entry.voided ? 'text-ink-3 line-through' : ''}`}
                        >
                          {entry.description}
                        </p>
                        <p className="flex items-center gap-2 text-[13px] text-ink-3">
                          {new Intl.DateTimeFormat('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                          }).format(new Date(entry.occurredAt))}
                          {entry.voided ? <Badge tone="neutral">Excluído</Badge> : null}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <p
                          className={`tabular font-bold ${
                            entry.voided ? 'text-ink-3 line-through' : outflow ? 'text-bad' : 'text-ok'
                          }`}
                        >
                          {outflow ? '−' : '+'} {formatCurrency(entry.amountCents)}
                        </p>
                        {isAdmin && entry.deletable ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={Trash2}
                            aria-label="Excluir lançamento"
                            title="Excluir lançamento"
                            onClick={() =>
                              setDeleteTarget({
                                id: entry.id,
                                description: entry.description,
                                amountCents: entry.amountCents,
                                outflow,
                              })
                            }
                          />
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </>
      )}

      {deleteTarget ? (
        <DeleteEntryModal
          groupId={groupId}
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      ) : null}
    </section>
  );
}

function DeleteEntryModal({
  groupId,
  target,
  onClose,
}: {
  groupId: string;
  target: { id: string; description: string; amountCents: number; outflow: boolean };
  onClose: () => void;
}) {
  const remove = useDeleteTransaction(groupId);

  return (
    <Modal title="Excluir lançamento" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-ink-2">
          <strong>{target.description}</strong> ({target.outflow ? '−' : '+'}{' '}
          {formatCurrency(target.amountCents)}) sai das receitas e despesas do mês, e o saldo do
          caixa volta a ser o de antes dele.
        </p>
        <p className="text-[13px] text-ink-3">
          Ele continua visível como “Excluído” no relatório, para ninguém estranhar a mudança no
          saldo.
        </p>
        <Button
          full
          variant="danger"
          loading={remove.isPending}
          onClick={() => remove.mutate(target.id, { onSuccess: onClose })}
        >
          Excluir lançamento
        </Button>
        {remove.error ? (
          <p className="text-[12px] text-bad">
            {remove.error instanceof ApiError ? remove.error.message : 'Erro ao excluir.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

function CancelFeeModal({
  groupId,
  target,
  onClose,
}: {
  groupId: string;
  target: { feeId: string; memberName: string; title: string; amountCents: number };
  onClose: () => void;
}) {
  const cancel = useCancelFee(groupId);
  const [reason, setReason] = useState('');

  return (
    <Modal title="Cancelar cobrança" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-ink-2">
          A cobrança <strong>{target.title}</strong> de <strong>{target.memberName}</strong> (
          {formatCurrency(target.amountCents)}) deixa de valer e sai dos valores em aberto. O caixa
          não muda — ela ainda não tinha sido paga.
        </p>
        <Field label="Motivo (opcional)">
          <Input
            value={reason}
            maxLength={160}
            placeholder="Ex.: lançada em duplicidade"
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        <Button
          full
          variant="danger"
          loading={cancel.isPending}
          onClick={() =>
            cancel.mutate({ feeId: target.feeId, reason: reason.trim() }, { onSuccess: onClose })
          }
        >
          Cancelar cobrança
        </Button>
        {cancel.error ? (
          <p className="text-[12px] text-bad">
            {cancel.error instanceof ApiError ? cancel.error.message : 'Erro ao cancelar.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/** Data local em AAAA-MM-DD — toISOString daria o dia em UTC. */
function todayISO(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Cobrança lançada pelo admin.
 *
 * "Mensalidade" escolhida a dedo cobre quem entrou depois ou paga diferente —
 * quem já tem a daquele mês é ignorado. "Avulsa" é para o racha da bola nova
 * ou do churrasco: tem descrição própria e pode haver várias no mês. As duas
 * caem em "Minhas mensalidades" de quem foi cobrado, com Pix e comprovante.
 */
function ChargeModal({
  groupId,
  defaultAmountCents,
  dueDay,
  onClose,
}: {
  groupId: string;
  defaultAmountCents: number;
  dueDay: number;
  onClose: () => void;
}) {
  const create = useCreateCharge(groupId);
  const members = useMembers(groupId);
  const today = todayISO();

  const [kind, setKind] = useState<'MONTHLY' | 'EXTRA'>('EXTRA');
  const [month, setMonth] = useState(today.slice(0, 7));
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState(() =>
    suggestedDueDate('EXTRA', today.slice(0, 7), dueDay),
  );
  const [everyone, setEveryone] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const active = (members.data ?? []).filter((member) => member.status === 'ACTIVE');
  const isMonthly = kind === 'MONTHLY';

  function changeKind(next: 'MONTHLY' | 'EXTRA') {
    setKind(next);
    setDueDate(suggestedDueDate(next, month, dueDay));
    if (next === 'MONTHLY' && !amount && defaultAmountCents > 0) {
      setAmount((defaultAmountCents / 100).toFixed(2).replace('.', ','));
    }
  }

  function toggle(memberId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }

  const amountCents = amount ? toCents(amount) : 0;
  const targets = everyone ? active.length : selected.size;
  const canSubmit =
    amountCents > 0 &&
    dueDate >= today &&
    targets > 0 &&
    (isMonthly ? Boolean(month) : description.trim().length >= 3);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        kind,
        ...(isMonthly ? { referenceMonth: month } : { description: description.trim() }),
        amountCents,
        dueDate,
        ...(everyone ? {} : { memberIds: [...selected] }),
      });
    } catch {
      // Erro exibido abaixo do formulário.
    }
  }

  if (create.data) {
    return (
      <Modal title="Cobrança lançada" onClose={onClose}>
        <div className="space-y-4">
          <p className="text-sm text-ink-2">
            {create.data.created > 0
              ? `${create.data.created} cobrança(s) lançada(s), total de ${formatCurrency(create.data.totalCents)}. Cada participante já vê em “Minhas mensalidades”.`
              : 'Nada lançado: todos os escolhidos já tinham a mensalidade deste mês.'}
            {create.data.skipped > 0 && create.data.created > 0
              ? ` ${create.data.skipped} já tinha(m) a mensalidade deste mês e foi(ram) ignorado(s).`
              : ''}
          </p>
          <Button full onClick={onClose}>
            Fechar
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Nova cobrança" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Tabs<'MONTHLY' | 'EXTRA'>
          active={kind}
          onChange={changeKind}
          tabs={[
            { key: 'EXTRA', label: 'Avulsa (racha)' },
            { key: 'MONTHLY', label: 'Mensalidade' },
          ]}
        />

        {isMonthly ? (
          <Field label="Mês" hint="Quem já tem a mensalidade deste mês é ignorado">
            <Input
              type="month"
              value={month}
              onChange={(event) => {
                setMonth(event.target.value);
                setDueDate(suggestedDueDate('MONTHLY', event.target.value, dueDay));
              }}
            />
          </Field>
        ) : (
          <Field label="O que está sendo cobrado">
            <Input
              placeholder="Ex.: Racha bola nova"
              maxLength={120}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              autoFocus
            />
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor por pessoa">
            <Input
              inputMode="decimal"
              placeholder="20,00"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </Field>
          <Field label="Vencimento">
            <Input
              type="date"
              min={today}
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
          </Field>
        </div>

        <Field label="Quem paga">
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={everyone ? 'primary' : 'secondary'}
                onClick={() => setEveryone(true)}
              >
                Todos ({active.length})
              </Button>
              <Button
                size="sm"
                variant={everyone ? 'secondary' : 'primary'}
                onClick={() => setEveryone(false)}
              >
                Escolher
              </Button>
            </div>

            {members.isLoading ? (
              <p className="text-[13px] text-ink-3">Carregando participantes…</p>
            ) : members.error ? (
              <p className="text-[13px] text-bad">
                Não foi possível carregar os participantes
                {members.error instanceof ApiError ? `: ${members.error.message}` : '.'}
              </p>
            ) : null}

            {everyone ? null : (
              <div className="max-h-56 space-y-2.5 overflow-y-auto rounded-lg border border-line p-3">
                {active.map((member) => (
                  <Checkbox
                    key={member.id}
                    checked={selected.has(member.id)}
                    onChange={() => toggle(member.id)}
                    label={member.nickname ?? member.user.name}
                  />
                ))}
              </div>
            )}
          </div>
        </Field>

        {amountCents > 0 && targets > 0 ? (
          <p className="text-[13px] text-ink-3">
            {targets} × {formatCurrency(amountCents)} = {formatCurrency(targets * amountCents)}
          </p>
        ) : null}

        {create.error ? (
          <p className="text-[13px] text-bad">
            {create.error instanceof ApiError ? create.error.message : 'Erro ao lançar.'}
          </p>
        ) : null}

        <div className="flex gap-3">
          <Button variant="secondary" full onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" full loading={create.isPending} disabled={!canSubmit}>
            Lançar cobrança
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Vencimento sugerido: na mensalidade, o dia do grupo naquele mês (ou daqui a
 * 5 dias, se já passou); na avulsa, uma semana — prazo de juntar o racha.
 */
function suggestedDueDate(kind: 'MONTHLY' | 'EXTRA', month: string, dueDay: number): string {
  const today = todayISO();
  const plusDays = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  if (kind === 'EXTRA') return plusDays(7);

  const regular = `${month}-${String(Math.min(Math.max(dueDay, 1), 28)).padStart(2, '0')}`;
  return regular >= today ? regular : plusDays(5);
}

type EntryKind = 'INCOME' | 'EXPENSE';

/**
 * Lançamento manual do admin. Entrada e despesa dividem o formulário porque
 * só a categoria muda; na API seguem caminhos diferentes — a despesa vira
 * registro próprio (editável, com categoria), a entrada vai direto ao caixa.
 */
function EntryModal({
  groupId,
  initialKind,
  onClose,
}: {
  groupId: string;
  initialKind: EntryKind;
  onClose: () => void;
}) {
  const createExpense = useCreateExpense(groupId);
  const createIncome = useCreateIncome(groupId);

  const [kind, setKind] = useState<EntryKind>(initialKind);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('FIELD_RENTAL');
  const [date, setDate] = useState(todayISO());

  const isIncome = kind === 'INCOME';
  const mutation = isIncome ? createIncome : createExpense;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const amountCents = Math.round(Number(amount.replace(',', '.')) * 100);
    try {
      if (isIncome) {
        await createIncome.mutateAsync({
          description: description.trim(),
          amountCents,
          receivedAt: date,
        });
      } else {
        await createExpense.mutateAsync({
          description: description.trim(),
          category,
          amountCents,
          incurredAt: date,
        });
      }
      onClose();
    } catch {
      // Erro exibido no campo.
    }
  }

  return (
    <Modal title={isIncome ? 'Lançar entrada' : 'Lançar despesa'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Tabs<EntryKind>
          active={kind}
          onChange={setKind}
          tabs={[
            { key: 'INCOME', label: 'Entrada' },
            { key: 'EXPENSE', label: 'Despesa' },
          ]}
        />

        <Field
          label="Descrição"
          error={mutation.error instanceof ApiError ? mutation.error.message : undefined}
        >
          <Input
            placeholder={isIncome ? 'Ex.: Diária de avulsos' : 'Ex.: Aluguel do campo'}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            autoFocus
          />
        </Field>

        {isIncome ? null : (
          <Field label="Categoria">
            <select
              className="input"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="FIELD_RENTAL">Aluguel do campo</option>
              <option value="WATER">Água</option>
              <option value="BIBS">Coletes</option>
              <option value="BALL">Bola</option>
              <option value="REFEREE">Arbitragem</option>
              <option value="BARBECUE">Churrasco</option>
              <option value="EQUIPMENT">Equipamento</option>
              <option value="OTHER">Outros</option>
            </select>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor">
            <Input
              inputMode="decimal"
              placeholder={isIncome ? '50,00' : '800,00'}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </Field>

          <Field label="Data">
            <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" full onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            full
            loading={mutation.isPending}
            disabled={description.trim().length < 2 || !amount}
          >
            Lançar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** "2026-08" → "Agosto/2026". */
function formatReference(reference: string): string {
  const [year, month] = reference.split('-').map(Number);
  // A data é meia-noite UTC do dia 1; formatada no fuso local (UTC−3) cairia
  // no último dia do mês anterior e outubro viraria "setembro".
  const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1, 1)),
  );
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}/${year}`;
}
